// Funding › Risk & settings: how long QOR's money at each processor lasts, deposits not wired on yet, what partners
// owe against the capital QOR may put at risk; the other approvals (setting changes, lifting a freeze, points pools);
// and the platform-wide funding settings.
import { shortDate } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fundsApi, type FundsHealth, type SettingValue } from '../api';
import { ApprovalsTable } from '../components/Approvals';
import { SettingsForm } from '../components/SettingsForm';
import type { FundingTabProps } from './Funding';
import { PLATFORM_FUNDING_SECTIONS } from './fundingSettings';
import { providerLabel } from './fundingLabels';

export function FundingRiskTab({
  editable,
  busy,
  setBusy,
  done,
  decide,
  health: h,
}: FundingTabProps & { health: FundsHealth | null }) {
  const [showSettings, setShowSettings] = useState(false);
  const settings = useQuery({ queryKey: ['funds-settings', 'platform'], queryFn: () => fundsApi.settings(null) });
  const others = useQuery({
    queryKey: ['approvals', 'pending', 'other'],
    queryFn: () => fundsApi.approvals('pending', 1, 'other'),
  });

  async function saveSettings(change: { set: Record<string, SettingValue>; clear: string[] }) {
    setBusy(true);
    const r = await fundsApi.changeSettings(null, change);
    await done(
      r.ok,
      r.ok
        ? r.data.approvalId
          ? 'Saved; the changes marked "Needs approval" wait for a second system administrator.'
          : 'Platform funding settings saved.'
        : r.error,
    );
    return r.ok ? {} : r.problems;
  }

  return (
    <>
      {h && (
        <div className="qc-panel">
          <div className="qc-panel-title">
            <h3>Cash-flow risk</h3>
            <span>
              How long QOR&apos;s money at each processor lasts, deposits not wired on yet, and what partners owe QOR
              against the capital it may put at risk.
            </span>
          </div>
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Processor</th>
                <th>Money there</th>
                <th>Loaded a day (7 days)</th>
                <th>Lasts</th>
                <th>Deposits not wired on</th>
              </tr>
            </thead>
            <tbody>
              {h.risk.markets.map((m) => (
                <tr key={`${m.provider}-${m.currency}`}>
                  <td>
                    {providerLabel(m.provider)} {m.currency}
                  </td>
                  <td>{m.balance}</td>
                  <td>{m.dailyLoad}</td>
                  <td>{m.runwayDays === null ? '—' : `${m.runwayDays} days`}</td>
                  <td>
                    {m.unwired}
                    {m.unwiredSince && (
                      <>
                        <br />
                        <small>
                          oldest {shortDate(m.unwiredSince)} ({m.unwiredBusinessDays} business day
                          {m.unwiredBusinessDays === 1 ? '' : 's'})
                        </small>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {h.risk.exposure.length === 0 ? (
            <p className="qc-empty" style={{ marginTop: 12 }}>
              Partners owe QOR nothing.
            </p>
          ) : (
            <table className="qc-plain-table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Owed to QOR</th>
                  <th>Capital at risk</th>
                  <th>Used</th>
                  <th>If the 3 largest never pay</th>
                  <th>Largest debtors</th>
                </tr>
              </thead>
              <tbody>
                {h.risk.exposure.map((e) => (
                  <tr key={e.currency}>
                    <td>{e.currency}</td>
                    <td>
                      <strong>{e.exposure}</strong>
                    </td>
                    <td>{e.capital}</td>
                    <td>{e.usedPercent === null ? '—' : `${e.usedPercent}%`}</td>
                    <td>{e.stressLoss}</td>
                    <td>
                      {e.debtors.slice(0, 3).map((d) => (
                        <div key={d.programId}>
                          <Link to={`/partners/${d.programId}?tab=funding`}>{d.name}</Link> {d.owed}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>Other approvals</h3>
          <span>Setting changes, lifting a freeze of loads, and points pools.</span>
        </div>
        {others.data && (
          <ApprovalsTable
            items={others.data.items}
            busy={busy}
            showProgram
            onDecide={(a, ok) => void decide(a, ok)}
            onChanged={(ok, message) => void done(ok, message)}
          />
        )}
      </div>

      <div className="qc-panel" style={{ marginTop: 20 }}>
        <div className="qc-panel-title">
          <h3>QOR settings</h3>
          <span>
            QOR-wide limits and alerts, and the defaults of every partner. Each partner&apos;s own values are in its
            Funding tab.
          </span>
        </div>
        <button
          type="button"
          className="qc-button-ghost"
          aria-expanded={showSettings}
          onClick={() => setShowSettings(!showSettings)}
        >
          {showSettings ? 'Hide QOR settings' : 'Show QOR settings'}
        </button>
        {showSettings && settings.data && (
          <div style={{ marginTop: 14 }}>
            <SettingsForm
              key={settings.dataUpdatedAt}
              rows={settings.data.filter((r) => r.group.startsWith('funding') || r.group === 'risk')}
              sections={PLATFORM_FUNDING_SECTIONS}
              editable={editable}
              busy={busy}
              onSave={saveSettings}
            />
          </div>
        )}
      </div>
    </>
  );
}
